// components/HubItemCard.jsx
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BsRobot,
  BsCodeSlash,
  BsPeopleFill,
  BsStarFill,
  BsArrowRight,
} from 'react-icons/bs';
import Card from '../../../components/Card';
import Badge from '../../../components/Badge';

const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toLocaleString();
};
const HubItemCard = ({ item, type = 'agent', index = 0 }) => {
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const getItemData = () => {
    if (type === 'agent') {
      return {
        id: item.id,
        title: item.info?.name || 'Unnamed Agent',
        subtitle: `@${item.author?.username || 'anonymous'}`,
        description: item.info?.short_description || '',
        image: item.info?.image,
        stats: item.info?.n_purchases || 0,
        badges: [],
        defaultIcon: <BsRobot size={28} />,
      };
    } else {
      return {
        id: item.id,
        title: item.title || 'Unnamed Program',
        subtitle: `@${item.author?.username || 'anonymous'}`,
        description: item.short_description || '',
        image: item.image,
        stats: item.n_purchases || 0,
        badges: [item.program_type],
        defaultIcon: <BsCodeSlash size={28} />,
      };
    }
  };

  const data = getItemData();

  const handleViewClick = () => {
    const baseUrl = 'https://app.quartermaster.io';
    let url;

    if (type === 'agent') {
      url = `${baseUrl}/agent-info-editor/${data.id}/preview`;
    } else {
      url = `${baseUrl}/program/${data.id}/preview`;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      whileHover={{ y: -4 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="h-100"
    >
      <Card className="h-100 position-relative overflow-hidden">
        <Card.Body className="d-flex flex-column">
          {/* Header with Image/Icon */}
          <div className="d-flex align-items-start mb-3">
            <div className="flex-shrink-0 position-relative">
              {data.image && !imageError ? (
                <img
                  src={data.image}
                  alt={data.title}
                  className="rounded"
                  style={{
                    width: '48px',
                    height: '48px',
                    objectFit: 'cover',
                  }}
                  onError={() => setImageError(true)}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center rounded bg-light"
                  style={{
                    width: '48px',
                    height: '48px',
                  }}
                >
                  {data.defaultIcon}
                </div>
              )}
            </div>

            <div className="flex-grow-1 ms-3" style={{ minWidth: 0 }}>
              <h6 className="mb-1 text-truncate fw-semibold">{data.title}</h6>
              <small className="text-success d-block">{data.subtitle}</small>
            </div>
          </div>

          {/* Description */}
          {data.description && (
            <p
              className="text-muted small mb-3"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {data.description}
            </p>
          )}

          {/* Badges */}
          {data.badges && data.badges.length > 0 && (
            <div className="d-flex flex-wrap gap-1 mb-3">
              {data.badges.slice(0, 3).map((badge, idx) => (
                <Badge key={idx} variant="secondary" size="sm">
                  {badge}
                </Badge>
              ))}
            </div>
          )}

          {/* Footer with Stats and Action Button on Same Line */}
          <div className="mt-auto">
            {/* Trust Score Stars (if applicable) */}
            {data.stats > 50 && (
              <div className="d-flex justify-content-center mb-2">
                <div className="d-flex align-items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <BsStarFill
                      key={i}
                      size={10}
                      className={
                        i < Math.min(5, Math.floor(data.stats / 100))
                          ? 'text-warning'
                          : 'text-muted opacity-25'
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Stats and Button Row */}
            <div className="d-flex align-items-center justify-content-between gap-2">
              {/* Owners Count */}
              <div className="d-flex align-items-center gap-2">
                <div
                  className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                  style={{
                    backgroundColor: 'rgba(4, 140, 99, 0.1)',
                    border: '1px solid rgba(4, 140, 99, 0.2)',
                    fontSize: '0.875rem',
                  }}
                >
                  <BsPeopleFill size={14} className="text-success" />
                  <span className="fw-semibold text-success">
                    {formatNumber(data.stats)}
                  </span>
                  <span className="text-muted">
                    {data.stats === 1 ? 'owner' : 'owners'}
                  </span>
                </div>

                {/* Animated indicator for high usage */}
                {data.stats > 100 && (
                  <motion.div
                    animate={{
                      scale: isHovered ? [1, 1.2, 1] : 1,
                    }}
                    transition={{
                      duration: 0.5,
                      repeat: isHovered ? Infinity : 0,
                      repeatDelay: 0.5,
                    }}
                  >
                    <span
                      className="badge rounded-circle p-1"
                      style={{
                        backgroundColor: 'rgba(4, 140, 99, 0.2)',
                        width: '8px',
                        height: '8px',
                      }}
                    />
                  </motion.div>
                )}
              </div>

              {/* View Button */}
              <button
                className="btn btn-sm btn-outline-success d-flex align-items-center gap-1"
                style={{
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  minWidth: '80px',
                }}
                onClick={handleViewClick}
              >
                View
                <BsArrowRight size={12} />
              </button>
            </div>
          </div>
        </Card.Body>

        {/* Subtle gradient overlay for popular items */}
        {data.stats > 1000 && (
          <div
            className="position-absolute bottom-0 start-0 w-100"
            style={{
              height: '3px',
              background: 'linear-gradient(90deg, #048c63, #10b981)',
              opacity: isHovered ? 1 : 0.6,
              transition: 'opacity 0.3s ease',
            }}
          />
        )}
      </Card>
    </motion.div>
  );
};

export default HubItemCard;
